import React, { ChangeEvent, forwardRef, useCallback, useMemo, useState } from 'react';

import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import { ITextareaProps } from './models';

import styled from './Textarea.scss';

/** Поле ввода */
export const Textarea = forwardRef<HTMLTextAreaElement, ITextareaProps>(
  (
    {
      className,
      size = 'md',
      label,
      value: propsValue,
      id,
      defaultValue = '',
      errorMessage,
      helpMessage,
      isHelpMessage,
      rows = 3,
      onBlur,
      onFocus,
      onChange,
      ...rest
    },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue || propsValue || '');
    const [isFocus, setIsFocus] = useState(false);

    /** Вычисляемое значение нужно, чтобы отображать внешнее значение инпута или внутреннее */
    const mergedValue = useMemo(() => {
      if (propsValue !== undefined) return propsValue;
      return value || '';
    }, [value, propsValue]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newVal = e.target.value;

        if (propsValue === undefined) {
          setValue(newVal);
        }

        onChange?.(e);
      },
      [onChange, propsValue],
    );

    const handleFocus = useCallback(
      (e) => {
        setIsFocus(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        setIsFocus(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const isSmallLabel = Boolean(mergedValue) || isFocus;
    const isPlaceholder = Boolean(!label || isSmallLabel);

    const rootClasses = classnames(styled.root, ...getClasses(styled, { size }), {
      [styled.error]: Boolean(errorMessage),
    });
    const containerClasses = classnames(className, styled.container);

    return (
      <div className={rootClasses}>
        <div className={containerClasses} data-size={size} data-elem="container" data-placeholder={isPlaceholder}>
          <div data-elem="label" data-size={size} className={styled.label} data-label-small={isSmallLabel}>
            {label}
          </div>

          <textarea
            {...rest}
            data-elem="input"
            data-size={size}
            ref={ref}
            rows={rows}
            value={mergedValue}
            data-id={id}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {isHelpMessage && (
          <>
            {errorMessage ? (
              <div data-elem="message" data-id="errorMsg">
                {errorMessage}
              </div>
            ) : (
              <div data-elem="message" data-id="helpMsg">
                {helpMessage}
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
